<template>
  <Dialog
    v-model:visible="displayModal"
    :header="t('templateBuilder.previewDisplayV4Modal.title')"
    class="preview-display-v4-modal"
    :style="{ width: '640px' }"
    :modal="true"
    :dismissable-mask="true"
  >
    <div class="preview-display-body">
      <!-- URL input card -->
      <div class="preview-display-url-card">
        <p class="preview-display-url-card__label">
          {{ t('templateBuilder.previewDisplayV4Modal.description') }}
        </p>
        <div class="preview-display-url-card__row">
          <span class="preview-display-url-card__prefix">{{ shopBaseUrl }}</span>
          <input
            v-model="previewUrlPath"
            type="text"
            :placeholder="t('templateBuilder.previewDisplayV4Modal.pathPlaceholder')"
            :disabled="previewUrlLoading"
            class="preview-display-url-card__input"
            @keyup.enter="confirmPreviewUrl"
          >
        </div>
        <p
          v-if="previewUrlError"
          class="preview-display-url-card__error"
        >
          {{ previewUrlError }}
        </p>
      </div>

      <!-- Mode button group -->
      <div class="preview-display-mode-group">
        <button
          class="preview-display-mode-group__btn"
          :class="{ 'preview-display-mode-group__btn--active': previewMode === 'visitor' }"
          @click="switchMode('visitor')"
        >
          <i class="far fa-user-secret" />
          {{ t('templateBuilder.previewDisplayV4Modal.modeVisitor') }}
        </button>
        <button
          class="preview-display-mode-group__btn"
          :class="{ 'preview-display-mode-group__btn--active': previewMode === 'contact' }"
          @click="switchMode('contact')"
        >
          <i class="far fa-address-book" />
          {{ t('templateBuilder.previewDisplayV4Modal.modeContact') }}
        </button>
      </div>

      <div
        v-if="availableShopLangs.length > 0"
        class="preview-display-langs"
      >
        <span class="preview-display-langs__label">
          {{ t('templateBuilder.modals.sendTestV4.previewIn') }}
        </span>
        <div class="preview-display-langs__list">
          <button
            v-for="language in availableShopLangs"
            :key="language.id"
            class="preview-display-langs__item"
            :class="{ 'preview-display-langs__item--active': selectedPreviewLang === language.id }"
            type="button"
            @click="selectPreviewLang(language.id)"
          >
            <img
              :src="`/images/flags/${language.id}.png`"
              :alt="language.id"
            >
            <span>{{ language.id.toUpperCase() }}</span>
          </button>
        </div>
      </div>

      <!-- Contacts card (only in contact mode) -->
      <div v-if="previewMode === 'contact'" class="preview-display-contacts-card">
        <div
          v-if="segmentConditions.length > 0"
          class="preview-display-conditions-card"
        >
          <div class="preview-display-conditions-card__header">
            <span class="preview-display-conditions-card__title">
              {{ t('templateBuilder.previewDisplayV4Modal.conditionSimulationTitle') }}
            </span>
          </div>
          <p class="preview-display-conditions-card__hint">
            {{ t('templateBuilder.previewDisplayV4Modal.conditionSimulationHint') }}
          </p>
          <div class="preview-display-conditions-card__list">
            <div
              v-for="(condition, index) in segmentConditions"
              :key="condition.key"
              class="preview-display-conditions-card__item"
            >
              <span class="preview-display-conditions-card__badge">C{{ index + 1 }}</span>
              <span class="preview-display-conditions-card__label">
                {{ t('templateBuilder.modals.sendTestV4.conditionLabel', { index: index + 1 }) }}
              </span>
              <i
                class="fa-light fa-circle-info preview-display-conditions-card__info"
                @mouseenter.stop="showConditionPopover($event, condition)"
                @mouseleave="hideConditionPopover"
              />
              <InputSwitch
                :model-value="conditionOverrides[condition.key]"
                class="preview-display-conditions-card__switch"
                @update:model-value="setConditionOverride(condition, $event)"
              />
            </div>
          </div>
        </div>

        <ContactListScroll
          :model-value="selectedContactId"
          :template-id="templateId"
          :initial-contact-info="selectedContactInfo"
          :raw-html="currentRawHtml"
          :all-contacts-only="true"
          @update:model-value="onContactSelected"
          @update:contact-info="onContactInfoUpdate"
          @update:segment-info="onSegmentInfoUpdate"
        />
      </div>
    </div>

    <OverlayPanel
      ref="conditionOverlayRef"
      class="preview-display-condition-overlay"
    >
      <div class="preview-display-condition-overlay__content">
        <SegmentationsPanel
          v-if="activeConditionPopover"
          :key="activeConditionPopover.id"
          :model-value="activeConditionPopover.config"
          size="small"
          :readonly="true"
        />
      </div>
    </OverlayPanel>

    <!-- Footer -->
    <div class="preview-display-footer">
      <Button
        :label="t('templateBuilder.modals.cancel')"
        icon="far fa-times"
        class="p-button-secondary"
        :disabled="previewUrlLoading"
        @click="displayModal = false"
      />
      <Button
        :label="previewUrlLoading ? t('templateBuilder.previewDisplayV4Modal.checking') : t('templateBuilder.previewDisplayV4Modal.open')"
        icon="far fa-eye"
        class="p-button-primary"
        :loading="previewUrlLoading"
        :disabled="previewUrlLoading"
        @click="confirmPreviewUrl"
      />
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  WritableComputedRef,
  Ref,
  watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import ContactListScroll from '@/components/contacts/ContactListScroll.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState, getCurrentTemplateVersion } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import { Lang, UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';
import { isDemoModeActive, demoOverrides } from '@/composables/DemoMode';

export default defineComponent({
  name: 'PreviewDisplayV4Modal',

  components: {
    Dialog,
    Button,
    InputSwitch,
    OverlayPanel,
    ContactListScroll,
    SegmentationsPanel,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const displayModal: WritableComputedRef<boolean> = computed({
      get: () => store.state.general.previewDisplayV4ModalVisible,
      set: (val: boolean) => {
        if (!val) store.commit('general/hidePreviewDisplayV4Modal');
      },
    });

    // ========== Template & Shop ==========
    const templateId = computed(() => TemplateEditorState.template?.id || undefined);
    const idShop = computed(() => UserState.activeShop?.id || 0);

    const shopBaseUrl = computed(() => {
      const url = (isDemoModeActive.value && demoOverrides.url)
        ? demoOverrides.url
        : (UserState.activeShop?.url || '');
      return url.replace(/\/+$/, '');
    });

    const availableShopLangs = computed<Lang[]>(() => {
      const langs = UserState.activeShop?.langs ?? [];
      const sorted = [...langs];
      sorted.sort((a, b) => {
        if (a.default) return -1;
        if (b.default) return 1;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    });

    const defaultShopLanguage = computed<string>(() => (
      availableShopLangs.value.find((language: Lang) => language.default)?.id
      ?? availableShopLangs.value[0]?.id
      ?? 'fr'
    ));
    const selectedPreviewLang = ref<string>(defaultShopLanguage.value);

    // ========== Raw HTML from iframe ==========
    const currentRawHtml = computed(() => {
      const version = getCurrentTemplateVersion(idShop.value);
      return version?.data?.html || '';
    });

    interface SegmentCondition {
      id: number;
      config: Record<string, any>;
      key: string;
    }

    interface SegmentInfoPayload {
      hasSegments: boolean;
      conditions: Array<{ id: number; config: Record<string, any> }>;
    }

    // ========== LocalStorage ==========
    const getStorageKey = () => {
      const id = templateId.value;
      return id ? `sendTestV4_${id}` : null;
    };

    const saveToStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      const data: Record<string, any> = {};
      data.previewMode = previewMode.value;
      if (selectedContactId.value) data.contactId = selectedContactId.value;
      if (selectedContactInfo.value) data.contactInfo = selectedContactInfo.value;
      if (previewUrlPath.value) data.path = previewUrlPath.value;
      if (selectedPreviewLang.value) data.previewLang = selectedPreviewLang.value;
      if (Object.keys(conditionOverrides.value).length > 0) {
        data.conditionOverrides = conditionOverrides.value;
      }
      setLocalStorageElement(key, JSON.stringify(data));
    };

    const restoreFromStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      try {
        const raw = getLocalStorageElement(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data.contactId) {
          selectedContactId.value = Number(data.contactId);
          previewMode.value = 'contact';
        } else if (data.previewMode === 'contact' || data.previewMode === 'visitor') {
          previewMode.value = data.previewMode;
        }
        if (data.contactInfo) selectedContactInfo.value = data.contactInfo;
        if (data.path) previewUrlPath.value = data.path;
        if (
          typeof data.previewLang === 'string'
          && availableShopLangs.value.some((language) => language.id === data.previewLang)
        ) {
          selectedPreviewLang.value = data.previewLang;
        } else {
          selectedPreviewLang.value = defaultShopLanguage.value;
        }
        if (data.conditionOverrides && typeof data.conditionOverrides === 'object') {
          pendingStoredConditionOverrides.value = data.conditionOverrides;
        }
      } catch {
        // Ignore parse errors
      }
    };

    // ========== Mode Selection ==========
    const previewMode = ref<'visitor' | 'contact'>('visitor');

    const switchMode = (mode: 'visitor' | 'contact') => {
      previewMode.value = mode;
      if (mode === 'visitor') {
        selectedContactId.value = null;
        selectedContactInfo.value = null;
      }
    };

    // ========== Contact Selection ==========
    const selectedContactId: Ref<number | null> = ref(null);
    const selectedContactInfo: Ref<{ name: string; email: string } | null> = ref(null);
    const segmentConditions: Ref<SegmentCondition[]> = ref([]);
    const conditionOverrides = ref<Record<string, boolean>>({});
    const pendingStoredConditionOverrides = ref<Record<string, boolean> | null>(null);
    const conditionOverlayRef = ref();
    const activeConditionPopover = ref<SegmentCondition | null>(null);
    let conditionPopoverHideTimeout: ReturnType<typeof setTimeout> | null = null;
    const getConditionKey = (config: Record<string, any>) => JSON.stringify(config);

    const onContactSelected = (contactId: number) => {
      selectedContactId.value = contactId;
    };

    const onContactInfoUpdate = (info: { name: string; email: string } | null) => {
      selectedContactInfo.value = info;
    };

    const synchronizeConditionOverrides = (conditions: SegmentCondition[]) => {
      const nextOverrides: Record<string, boolean> = {};
      conditions.forEach((condition) => {
        if (Object.prototype.hasOwnProperty.call(conditionOverrides.value, condition.key)) {
          nextOverrides[condition.key] = conditionOverrides.value[condition.key];
        } else if (
          pendingStoredConditionOverrides.value
          && Object.prototype.hasOwnProperty.call(pendingStoredConditionOverrides.value, condition.key)
        ) {
          nextOverrides[condition.key] = pendingStoredConditionOverrides.value[condition.key];
        } else {
          nextOverrides[condition.key] = true;
        }
      });
      conditionOverrides.value = nextOverrides;
      pendingStoredConditionOverrides.value = null;
    };

    const onSegmentInfoUpdate = (segmentInfo: SegmentInfoPayload) => {
      if (!segmentInfo?.hasSegments || !Array.isArray(segmentInfo.conditions)) {
        segmentConditions.value = [];
        conditionOverrides.value = {};
        return;
      }

      const mappedConditions: SegmentCondition[] = segmentInfo.conditions.map((condition) => ({
        id: condition.id,
        config: condition.config,
        key: getConditionKey(condition.config),
      }));
      segmentConditions.value = mappedConditions;
      synchronizeConditionOverrides(mappedConditions);
    };

    const setConditionOverride = (condition: SegmentCondition, value: boolean) => {
      conditionOverrides.value = {
        ...conditionOverrides.value,
        [condition.key]: value,
      };
    };

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      if (conditionPopoverHideTimeout) {
        clearTimeout(conditionPopoverHideTimeout);
        conditionPopoverHideTimeout = null;
      }
      activeConditionPopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      conditionPopoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activeConditionPopover.value = null;
      }, 150);
    };

    const selectPreviewLang = (langId: string) => {
      selectedPreviewLang.value = langId;
    };

    // ========== Preview URL ==========
    const previewUrlPath = ref('');
    const previewUrlError = ref('');
    const previewUrlLoading = ref(false);
    const confirmPreviewUrl = async () => {
      previewUrlError.value = '';
      previewUrlLoading.value = true;

      const path = previewUrlPath.value.trim();
      const fullUrl = shopBaseUrl.value + (path.startsWith('/') ? path : path ? `/${path}` : '');

      // Close any existing preview window
      if ((window as any).__spmPreviewWindow && !(window as any).__spmPreviewWindow.closed) {
        (window as any).__spmPreviewWindow.close();
      }

      const windowWidth = window.innerWidth * 0.95;
      const height = (windowWidth * window.innerHeight) / window.innerWidth;
      const previewWindow = window.open(
        'about:blank',
        'previewDisplayWindow_' + Date.now(),
        `width=${windowWidth}, height=${height}, top=${(window.innerHeight - height) / 2}, left=${(window.innerWidth - windowWidth) / 2}`,
      );
      (window as any).__spmPreviewWindow = previewWindow;

      try {
        saveToStorage();

        // In demo mode, skip script check (prospect site has no ShopImind tag — extension handles injection)
        if (!isDemoModeActive.value) {
          const checkResult = await nestCall(
            'post',
            NestApiType.DEFAULT,
            '/template/check-preview-script',
            {},
            '',
            { idShop: idShop.value, url: fullUrl },
          );

          if (!checkResult || checkResult.error) {
            previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }

          if (!checkResult.available) {
            previewUrlError.value = checkResult.error === 'fetch_failed'
              ? t('templateBuilder.previewDisplayV4Modal.errorFetchFailed')
              : t('templateBuilder.previewDisplayV4Modal.errorScriptNotFound');
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }
        }

        const currentVersion = getCurrentTemplateVersion(idShop.value);
        if (currentVersion) {
          const { data, params } = currentVersion;
          try {
            const response = await nestCall(
              'post',
              NestApiType.DEFAULT,
              '/template/save-preview-display-with-context',
              {},
              '',
              {
                ...params,
                ...data,
                contactId: selectedContactId.value || undefined,
                lang: selectedPreviewLang.value || defaultShopLanguage.value,
                conditionOverrides: Object.keys(conditionOverrides.value).length > 0
                  ? conditionOverrides.value
                  : undefined,
                previewOptions: {
                  annotateConditions: true,
                },
              },
            );

            const token = response?.token;
            if (!token) {
              previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
              previewUrlLoading.value = false;
              if (previewWindow && !previewWindow.closed) previewWindow.close();
              return;
            }

            const previewFullUrl = new URL(fullUrl);
            previewFullUrl.searchParams.append('spm-preview', token);

            if (previewWindow && !previewWindow.closed) {
              previewWindow.location.href = previewFullUrl.toString();
            }

            store.commit('general/hidePreviewDisplayV4Modal');
          } catch (error) {
            previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
            if (previewWindow && !previewWindow.closed) previewWindow.close();
          }
        }
      } catch (error) {
        previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
        if (previewWindow && !previewWindow.closed) previewWindow.close();
      }

      previewUrlLoading.value = false;
    };

    // ========== Modal lifecycle ==========
    watch(availableShopLangs, (langs) => {
      if (!langs.length) {
        selectedPreviewLang.value = 'fr';
        return;
      }
      if (!langs.some((language) => language.id === selectedPreviewLang.value)) {
        selectedPreviewLang.value = defaultShopLanguage.value;
      }
    }, { immediate: true });

    watch(displayModal, (newVal, oldVal) => {
      if (newVal) {
        restoreFromStorage();
        previewUrlError.value = '';
      } else if (oldVal) {
        saveToStorage();
      }
    }, { immediate: true });

    return {
      t,
      displayModal,
      previewMode,
      switchMode,
      templateId,
      shopBaseUrl,
      currentRawHtml,
      availableShopLangs,
      selectedPreviewLang,
      selectedContactId,
      selectedContactInfo,
      segmentConditions,
      conditionOverrides,
      onContactSelected,
      onContactInfoUpdate,
      onSegmentInfoUpdate,
      setConditionOverride,
      conditionOverlayRef,
      activeConditionPopover,
      showConditionPopover,
      hideConditionPopover,
      selectPreviewLang,
      previewUrlPath,
      previewUrlError,
      previewUrlLoading,
      confirmPreviewUrl,
    };
  },
});
</script>

<style scoped lang="scss">
// ---- Body ----
.preview-display-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem 0;
}

// ---- URL input card ----
.preview-display-url-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &__label {
    margin: 0 0 0.625rem;
    color: #64748b;
    font-size: 0.8125rem;
    line-height: 1.4;
    text-align: left;
  }

  &__row {
    display: flex;
    align-items: center;
  }

  &__prefix {
    display: inline-flex;
    align-items: center;
    padding: 0 0.75rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-right: none;
    border-radius: 4px 0 0 4px;
    color: #64748b;
    font-size: 0.8125rem;
    white-space: nowrap;
    height: 36px;
    box-sizing: border-box;
  }

  &__input {
    flex: 1;
    padding: 0 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 0 4px 4px 0;
    font-size: 0.875rem;
    color: #334155;
    outline: none;
    height: 36px;
    box-sizing: border-box;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: #94c840;
    }
  }

  &__error {
    margin: 0.5rem 0 0;
    color: #e24c4c;
    font-size: 0.8125rem;
  }
}

// ---- Mode button group ----
.preview-display-mode-group {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &__btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #64748b;
    background: #fff;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;

    &:first-child {
      border-right: 1px solid #e2e8f0;
    }

    &:hover {
      color: #334155;
      background: #f8fafc;
    }

    &--active {
      color: #fff;
      background: #94c840;

      &:hover {
        background: #84b438;
      }
    }
  }
}

.preview-display-langs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  padding: 0.45rem 0.6rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &__label {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
    white-space: nowrap;
  }

  &__list {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__item {
    border: 1px solid #dbe3ee;
    border-radius: 999px;
    background: #fff;
    color: #334155;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.45rem;
    cursor: pointer;
    transition: all 0.2s ease;

    img {
      width: 14px;
      height: 10px;
      border-radius: 2px;
      display: block;
    }

    span {
      font-size: 0.7rem;
      font-weight: 600;
      line-height: 1;
    }

    &:hover {
      border-color: #94c748;
      background: #f8fced;
    }

    &--active {
      border-color: #94c748;
      background: #f0f8dc;
      color: #365314;
    }
  }
}

// ---- Contacts card ----
.preview-display-contacts-card {
  height: 450px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.preview-display-conditions-card {
  border-bottom: 1px solid #e2e8f0;
  padding: 0.7rem 0.85rem;
  background: #ffffff;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.35rem;
  }

  &__title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__hint {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.35;
    text-align: left;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
  }

  &__badge {
    font-size: 0.68rem;
    font-weight: 700;
    color: #5f8f1a;
    background: #ecf8d8;
    border-radius: 999px;
    padding: 0.08rem 0.45rem;
    white-space: nowrap;
  }

  &__label {
    font-size: 0.79rem;
    font-weight: 600;
    color: #334155;
  }

  &__info {
    margin-left: auto;
    color: #94a3b8;
    cursor: pointer;
    font-size: 0.85rem;

    &:hover {
      color: #64748b;
    }
  }

  &__switch {
    margin-left: 0.2rem;
  }
}

// ---- Footer ----
.preview-display-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 0 0;
  border-top: 1px solid #f1f5f9;
  margin-top: 0.25rem;
}
</style>

<style lang="scss">
.preview-display-condition-overlay {
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .p-overlaypanel-content {
    padding: 0;
  }

  &__content {
    min-width: 320px;
    max-width: 520px;
    min-height: 60px;
    padding: 1rem;
    background: #f8fafc;
  }
}
</style>
